import React from 'react';
import PropTypes from 'prop-types';
import {TextField, InputAdornment, Autocomplete} from '@mui/material';
import SearchIcon from '../../../assets/search-grey.svg';
import FileIcon from '../../../assets/file.svg';
import FolderIcon from '../../../assets/folder-filled.svg';
import ExternalFolderIcon from '../../../assets/folder-external.svg';
import {useNavigate} from 'react-router-dom';
import {setSelectedFolder} from '../../../data/folders';
import {useDispatch} from 'react-redux';
import parse from 'html-react-parser';
import {decode} from 'html-entities';
import {DOCUMENT_INGESTION_TYPES, FOLDER_PURPOSES} from '../../../constants';

const SearchBar = ({searchData, sx = {}}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleSelect = (option) => {
    if (option?.documentId) {
      if (option?.ingestionState?.type === DOCUMENT_INGESTION_TYPES.pdfReview) {
        navigate(`/documents/pdf-review/${option?.documentId}`);
      } else {
        navigate(`/documents/${option?.documentId}`);
      }
    } else {
      dispatch(setSelectedFolder({selectedFolder: option}));
      navigate(`/folders/${option?.id}/documents`);
    }
  };
  return (
    <div>
      <Autocomplete
        onChange={(event, value) => {
          if (value) {
            handleSelect(value);
          }
        }}
        sx={{
          width: '400px',
          marginTop: '1.5rem',
          ...sx,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            height: '40px',
            backgroundColor: '#FFFFFF',
            '&.Mui-focused fieldset': {
              border: '2px solid #34A290',
            },
            fieldset: {
              border: '1px solid #DFE5E2',
            },
          },
          '& .MuiInputBase-input': {
            fontWeight: '400',
            fontSize: '14px',
          },
        }}
        options={searchData || []}
        getOptionLabel={(option) => {
          // For folders, display with year prefix if year exists
          if (!option?.documentId && option?.year) {
            return `${option.year} - ${option.name}`;
          }
          return option?.name;
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              marginTop: '0.5rem',
              boxShadow: '0px 2px 12px 2px rgba(15, 30, 36, 0.12)',
            },
          },
        }}
        renderOption={(props, option) => {
          const isExternalFolder =
            !option?.documentId &&
            option?.purpose === FOLDER_PURPOSES.pdfReview;
          const isExternalDocument =
            option?.documentId &&
            option?.ingestionState?.type === DOCUMENT_INGESTION_TYPES.pdfReview;
          const iconSrc = option?.documentId
            ? FileIcon
            : isExternalFolder
              ? ExternalFolderIcon
              : FolderIcon;

          return (
            <li
              {...props}
              key={option?.id || option?.documentId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 12px',
                fontSize: '16px',
                fontWeight: '500',
                lineHeight: '140%',
                letterSpacing: '-0.12px',
                color: '#162523',
              }}
            >
              <img
                src={iconSrc}
                alt={
                  option?.documentId
                    ? 'File'
                    : isExternalFolder
                      ? 'External Folder'
                      : 'Folder'
                }
                style={{width: 20, height: 20, flexShrink: 0}}
              />
              <span>
                {parse(
                  decode(
                    // For folders, display with year prefix if year exists
                    !option?.documentId && option?.year
                      ? `${option.year} - ${option.name}`
                      : option.name
                  )
                )}
                {isExternalDocument && (
                  <span style={{color: '#819190'}}> (external)</span>
                )}
              </span>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search reports"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{marginLeft: '8px', marginRight: '8px'}}
                >
                  <img
                    src={SearchIcon}
                    alt="Search"
                    style={{width: 20, height: 20}}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input::placeholder': {
                color: '#819190',
                opacity: 1,
              },
            }}
          />
        )}
      />
    </div>
  );
};

export default SearchBar;

SearchBar.propTypes = {
  searchData: PropTypes.array.isRequired,
  sx: PropTypes.object,
};
